import React from 'react'
import { FaCheck } from 'react-icons/fa'
import styled from 'styled-components'

export interface PillFilterOption<T extends string> {
  value: T
  label: string
  count?: number
}

interface PillFilterProps<T extends string> {
  options: PillFilterOption<T>[]
  value: T
  onChange: (value: T) => void
  ariaLabel?: string
}

export function PillFilter<T extends string>({
  options,
  value,
  onChange,
  ariaLabel
}: PillFilterProps<T>) {
  return (
    <PillRow role="group" aria-label={ariaLabel}>
      {options.map(option => (
        <Pill
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          $isActive={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.count === undefined ? option.label : `${option.label} · ${option.count}`}
        </Pill>
      ))}
    </PillRow>
  )
}

interface PillToggleProps {
  label: string
  isActive: boolean
  onChange: (isActive: boolean) => void
}

export const PillToggle: React.FC<PillToggleProps> = ({ label, isActive, onChange }) => (
  <ToggleButton
    type="button"
    aria-pressed={isActive}
    $isActive={isActive}
    onClick={() => onChange(!isActive)}
  >
    {isActive && <FaCheck />}
    {label}
  </ToggleButton>
)

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const PillBase = styled.button<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;
  border-radius: 999px;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: ${({ $isActive }) => ($isActive ? 600 : 500)};
  white-space: nowrap;
  cursor: pointer;
  transition: 0.2s;

  svg {
    width: ${({ theme }) => theme.fontSize.xs}rem;
    height: ${({ theme }) => theme.fontSize.xs}rem;
  }
`

const Pill = styled(PillBase)`
  border: 1px solid
    ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.neutralGray)};
  background: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primary : theme.colors.neutralWhite};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.neutralWhite : theme.colors.metalGray};

  &:hover {
    filter: brightness(0.97);
  }
`

const ToggleButton = styled(PillBase)`
  border: 1px solid
    ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.neutralGray)};
  background: ${({ theme }) => theme.colors.neutralWhite};
  color: ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.metalGray)};

  &:hover {
    filter: brightness(0.97);
  }
`
